import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Message « vidéo prête » affiché uniquement quand l'événement arrive alors que
 * l'application est ouverte. Aucun rattrapage au montage : les anciennes lignes
 * restent dans la cloche jusqu'à l'ouverture du tiroir, qui seul marque lu.
 */

interface RealtimeNotification {
  id: string;
  kind: string;
  video_id: string | null;
  title: string | null;
  slug: string | null;
}

const TOAST_DURATION_MS = 8000;

export const VideoReadyNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const shown = new Set<string>();

    const show = (notification: RealtimeNotification) => {
      if (cancelled || shown.has(notification.id)) return;
      shown.add(notification.id);
      const target = notification.slug || notification.video_id;
      toast.success("Ta vidéo est publiée", {
        description: notification.title || "Elle est maintenant visible.",
        duration: TOAST_DURATION_MS,
        action: target
          ? { label: "Voir", onClick: () => navigate(`/video/${target}`) }
          : undefined,
      });
    };

    const channel = supabase
      .channel("video-ready-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as RealtimeNotification;
          if (row.kind !== "video_ready") return;
          show(row);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user, navigate]);

  return null;
};

export default VideoReadyNotifications;
